import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '../../components/layout/AdminLayout'

export default function TransactionsIndex({ transactions, filters, pagination, stats }) {
  const [selectedType, setSelectedType] = useState(filters?.type || '')
  const [selectedStatus, setSelectedStatus] = useState(filters?.status || '')

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(price)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleFilterChange = (type, status) => {
    router.get('/transactions', {
      type: type || undefined,
      status: status || undefined
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    }
    const labels = {
      pending: 'En attente',
      completed: 'Complété',
      failed: 'Échoué',
      refunded: 'Remboursé'
    }
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badges[status]}`}>
        {labels[status] || status}
      </span>
    )
  }

  const getTypeBadge = (type) => {
    const labels = {
      lead_purchase: 'Achat de lead',
      credit_purchase: 'Achat de crédits',
      refund: 'Remboursement'
    }
    return labels[type] || type
  }

  return (
    <AdminLayout>
      <Head title="Transactions - VitrineImmo" />

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Transactions</h1>
          <p className="text-gray-600 mt-1">
            Historique de vos achats de leads et crédits
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Solde actuel</div>
              <div className="text-2xl font-bold text-primary-600 mt-1">
                {formatPrice(stats.currentBalance)}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Total dépensé</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {formatPrice(stats.totalSpent)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Ce mois: {formatPrice(stats.thisMonthSpent)}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Crédits achetés</div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                +{formatPrice(stats.totalCredits)}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Remboursements</div>
              <div className="text-2xl font-bold text-orange-600 mt-1">
                {formatPrice(stats.totalRefunded)}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtres</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Type Filter */}
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                id="type"
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value)
                  handleFilterChange(e.target.value, selectedStatus)
                }}
                className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
              >
                <option value="">Tous les types</option>
                <option value="lead_purchase">Achat de lead</option>
                <option value="credit_purchase">Achat de crédits</option>
                <option value="refund">Remboursement</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                id="status"
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value)
                  handleFilterChange(selectedType, e.target.value)
                }}
                className="w-full rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-primary-500 focus:ring-2 focus:ring-offset-1 focus:outline-none"
              >
                <option value="">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="completed">Complété</option>
                <option value="failed">Échoué</option>
                <option value="refunded">Remboursé</option>
              </select>
            </div>

            {/* Clear Filters Button */}
            {(selectedType || selectedStatus) && (
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedType('')
                    setSelectedStatus('')
                    handleFilterChange('', '')
                  }}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Réinitialiser
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="w-12 h-12 mx-auto text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune transaction</h3>
              <p className="mt-1 text-sm text-gray-500">
                {(selectedType || selectedStatus)
                  ? 'Aucune transaction ne correspond à vos filtres.'
                  : 'Vous n\'avez pas encore de transactions.'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(transaction.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {getTypeBadge(transaction.transactionType)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {transaction.description || '-'}
                          {transaction.lead && (
                            <Link
                              href={`/leads/${transaction.lead.id}`}
                              className="block text-xs text-primary-600 hover:text-primary-700 mt-1"
                            >
                              {transaction.lead.firstName} {transaction.lead.lastName}
                            </Link>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(transaction.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <span className={
                            transaction.transactionType === 'refund' || transaction.transactionType === 'credit_purchase'
                              ? 'text-green-600'
                              : 'text-gray-900'
                          }>
                            {transaction.transactionType === 'refund' || transaction.transactionType === 'credit_purchase' ? '+' : '-'}
                            {formatPrice(transaction.finalPrice)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => router.get(`/transactions?page=${pagination.currentPage - 1}`)}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => router.get(`/transactions?page=${pagination.currentPage + 1}`)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Suivant
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Page <span className="font-medium">{pagination.currentPage}</span> sur{' '}
                        <span className="font-medium">{pagination.totalPages}</span> ({pagination.totalCount} transactions)
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
